import moment from "moment-timezone";

import type {Safelisting, SafelistingDay} from "../types";

export function isSafelistingExcludedFromQuota(safelisting: Pick<Safelisting, "override_count_toward_total">): boolean {
  return safelisting.override_count_toward_total === 1;
}

export function getSafelistingDayStatusLabel(status: SafelistingDay["status"]): string {
  switch (status) {
    case "active":
      return "Active";
    case "cancelled":
      return "Cancelled";
    case "skipped":
      return "Skipped";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export function getSafelistingDayStatusBadgeVariant(
  status: SafelistingDay["status"],
): "success" | "error" | "secondary" {
  switch (status) {
    case "active":
      return "success";
    case "cancelled":
      return "error";
    case "skipped":
      return "secondary";
    default:
      return "secondary";
  }
}

export function isPastCalendarDate(date: string, timeZone: string): boolean {
  const today = moment.tz(timeZone).format("YYYY-MM-DD");
  return date < today;
}

/** Hint when past days were cancelled and the record was updated after create (no correction audit API). */
export function shouldShowAccountabilityAdjustedHint(
  safelisting: Safelisting,
  timeZone: string,
): boolean {
  const hasPastCancelled = (safelisting.days ?? []).some(
    (day) => day.status === "cancelled" && isPastCalendarDate(day.date, timeZone),
  );
  if (!hasPastCancelled || !safelisting.updated_at || !safelisting.created_at) {
    return false;
  }
  return moment(safelisting.updated_at).isAfter(moment(safelisting.created_at));
}

export function isSafelistingDaysInARowOverride(value: number | undefined | null): boolean {
  return value === 1;
}

/** Total-days quota override (reason and/or server-computed overage days). */
export function hasSafelistingLimitOverride(
  safelisting: Pick<Safelisting, "override_reason" | "override_limit_days">,
): boolean {
  return Boolean(
    safelisting.override_reason?.trim() || (safelisting.override_limit_days ?? 0) > 0,
  );
}

export function hasCreateTimeLimitOverride(safelisting: Safelisting): boolean {
  return (
    hasSafelistingLimitOverride(safelisting) ||
    isSafelistingDaysInARowOverride(safelisting.override_days_in_a_row)
  );
}

export function getSafelistingAdjustmentLabels(
  safelisting: Safelisting,
  timeZone: string,
): string[] {
  const labels: string[] = [];
  if (isSafelistingExcludedFromQuota(safelisting)) {
    labels.push("Excluded from quota");
  }
  if (shouldShowAccountabilityAdjustedHint(safelisting, timeZone)) {
    labels.push("Accountability adjusted");
  }
  return labels;
}

export function getSafelistingCancelledPastDays(
  safelisting: Safelisting,
  timeZone: string,
): SafelistingDay[] {
  return (safelisting.days ?? [])
    .filter((day) => day.status === "cancelled" && isPastCalendarDate(day.date, timeZone))
    .toSorted((a, b) => a.date.localeCompare(b.date));
}

export function hasSafelistingAdjustments(safelisting: Safelisting, timeZone: string): boolean {
  return getSafelistingAdjustmentLabels(safelisting, timeZone).length > 0;
}
